export const setupChartInteractions = (chartContext: any, config: any) => {
  const el = config.globals.dom.baseEl
  const chartType: string | undefined = chartContext.w?.config?.chart?.type
  const ZOOMABLE_TYPES = ['line', 'area', 'bar', 'scatter', 'candlestick', 'heatmap', 'rangeBar']
  if (!chartType || !ZOOMABLE_TYPES.includes(chartType)) return

  if (el) {
    // Capture the initial fitted bounds at mount time.
    // At this point the chart has auto-fitted all data including
    // negative shrimpAge values, so minX is e.g. -13, not 0.
    const absoluteMinX: number = chartContext.w.globals.minX ?? -Infinity

    const safeZoomX = (min: number, max: number) => {
      const series = chartContext.w?.globals?.series
      if (!Array.isArray(series) || series.length === 0) return
      if (series.some((s) => !Array.isArray(s) || s.length === 0)) return
      chartContext.zoomX(min, max)
    }

    // Custom Wheel Handler (Zoom & Horizontal Pan)
    // Only intercept when user holds Ctrl/Meta (zoom intent) or scrolls horizontally (pan).
    // Plain vertical wheel passes through to the page so scrolling is not frozen.
    const wheelHandler = (e: WheelEvent) => {
      const isHorizontal = Math.abs(e.deltaX) > Math.abs(e.deltaY)
      const isZoomGesture = e.ctrlKey || e.metaKey
      if (!isHorizontal && !isZoomGesture) return

      const { minX, maxX, gridWidth } = chartContext.w.globals
      if (typeof minX !== 'number' || typeof maxX !== 'number') return

      const plotEl = el.querySelector('.apexcharts-inner')
      const plotRect = plotEl?.getBoundingClientRect()
      if (!plotRect) return

      e.preventDefault()

      const plotWidth = gridWidth || plotRect.width
      const range = maxX - minX

      // Horizontal Scroll -> Pan
      if (isHorizontal) {
        const shift = (e.deltaX / plotWidth) * range
        let newMin = minX + shift
        let newMax = maxX + shift

        if (newMin < absoluteMinX) {
          newMin = absoluteMinX
          newMax = absoluteMinX + range
        }

        safeZoomX(newMin, newMax)
        return
      }

      // Ctrl/Meta + Vertical Scroll -> Zoom
      const mouseX = e.clientX - plotRect.left
      const mouseRatio = Math.max(0, Math.min(1, mouseX / plotWidth))
      const zoomFactor = 0.05
      const isZoomIn = e.deltaY < 0

      const MIN_RANGE = 5
      const MAX_RANGE = 180

      let newRange = isZoomIn ? range * (1 - zoomFactor) : range * (1 + zoomFactor)
      if (newRange < MIN_RANGE) newRange = MIN_RANGE
      if (newRange > MAX_RANGE) newRange = MAX_RANGE

      if (Math.abs(newRange - range) < 0.01) return

      const dataX = minX + mouseRatio * range
      let newMin = dataX - mouseRatio * newRange
      let newMax = newMin + newRange

      if (newMin < absoluteMinX) {
        newMin = absoluteMinX
        newMax = absoluteMinX + newRange
      }

      safeZoomX(newMin, newMax)
    }

    // Custom Drag Handler
    let isDragging = false
    let startX = 0
    let startMinX = 0
    let startMaxX = 0
    let plotWidth = 0

    const mouseDownHandler = (e: MouseEvent) => {
      if (e.button !== 0) return
      isDragging = true
      startX = e.clientX
      const globals = chartContext.w.globals
      startMinX = globals.minX
      startMaxX = globals.maxX

      const plotEl = el.querySelector('.apexcharts-inner')
      const plotRect = plotEl?.getBoundingClientRect()
      plotWidth = globals.gridWidth || plotRect?.width || el.clientWidth

      el.style.cursor = 'grabbing'
    }

    const mouseMoveHandler = (e: MouseEvent) => {
      if (!isDragging) return
      e.preventDefault()

      const deltaX = e.clientX - startX
      const range = startMaxX - startMinX

      // Dragging Right (positive delta) -> View moves Left (decrease min)
      const shift = (deltaX / plotWidth) * range
      let newMin = startMinX - shift
      let newMax = startMaxX - shift

      // Clamp to the initial fitted minimum (captures negative shrimpAge)
      if (newMin < absoluteMinX) {
        newMin = absoluteMinX
        newMax = absoluteMinX + range
        // Rebase anchor so dragging back right works immediately
        startX = e.clientX
        startMinX = newMin
        startMaxX = newMax
      }

      safeZoomX(newMin, newMax)
    }

    const mouseUpHandler = () => {
      isDragging = false
      el.style.cursor = 'default'
    }

    el.addEventListener('wheel', wheelHandler, { passive: false })
    el.addEventListener('mousedown', mouseDownHandler, true)
    el.addEventListener('mousemove', mouseMoveHandler, true)
    el.addEventListener('mouseup', mouseUpHandler, true)
    el.addEventListener('mouseleave', mouseUpHandler, true)
  }
}
